/**
 * หน้าจอพนักงาน — ครึ่ง "พนักงาน" ของหน้าจอ `DepartmentsEmployeesScreen` เดิม
 * (`WO-P2-UI-001`)
 *
 * **โค้ดถูกย้ายมาทั้งท่อน ไม่ได้เขียนใหม่** และยังเรียก client ตัวเดิมทุกตัว
 * (`fetchEmployees`/`createEmployee`/`setEmployeeDepartment`/`setEmployeePlantAccess`)
 * ไม่มีเส้นทาง API ใหม่แม้แต่เส้นเดียว
 *
 * ปิดสามเส้นทางของ contract: `POST /v1/admin/employees` (`WO-P1-M2-012`) และ
 * sub-resource สองเส้นของพนักงาน `POST /v1/admin/employees/{id}/departments` กับ
 * `.../plant-access` (`WO-P1-M2-013`) โดยอ่านรายชื่อตั้งต้นจาก
 * `GET /v1/admin/employees`
 *
 * ⚠️ หน้านี้ยังอ่าน `GET /v1/admin/departments` ด้วย **แต่ไม่ได้แสดงรายการแผนก** —
 * รายการนั้นเป็นตัวเลือกของช่อง "แผนก" ในฟอร์มผูกสังกัด (contract ไม่มีเส้นทางอื่น
 * ที่คืนชื่อแผนกให้เลือก) การตัดคำขอนี้ทิ้งเพื่อให้หน้า "พนักงาน" เรียกแต่เส้นทางของ
 * พนักงาน จะทำให้ผูกแผนกให้ใครไม่ได้อีกเลย ส่วนหน้าจอที่ **แสดงและสร้าง** แผนกคือ
 * `DepartmentsScreen`
 *
 * การ gate สิทธิ์เป็นแบบเดียวกับ `PermissionMatrixScreen`/`SystemRolesScreen`:
 * `RBAC_ADMIN` + `VIEW` เป็นสิทธิ์ของ **การเปิดหน้าจอนี้** (ด่านอยู่ที่ route) ส่วน
 * `RBAC_ADMIN` + `EDIT` เป็นสิทธิ์ของ **ทุกปุ่มที่เขียน** ซึ่งถูกบังคับข้างในนี้
 *
 * **สามเรื่องที่หน้าจอนี้ต้องพูดออกมาให้ดัง ไม่ใช่ซ่อนไว้:**
 *
 * 1. **พนักงานที่เพิ่งสร้างยังเข้าสู่ระบบไม่ได้** — `POST /admin/employees` สร้าง
 *    employee master เท่านั้น ไม่สร้าง credential (`employee_credential` เป็นคนละ
 *    ตารางและคนละเส้นทาง) ผู้ดูแลที่ไม่รู้ข้อนี้จะสร้างพนักงานแล้วบอกเขาว่า
 *    "ล็อกอินได้แล้วนะ" ซึ่งไม่จริง
 * 2. **แผนกหลักและโรงงานหลักเป็น singleton ที่ "ย้าย" ไม่ใช่ "เพิ่ม"** — เซิร์ฟเวอร์
 *    ปลดธงของอันเดิมในธุรกรรมเดียวกับที่ติดธงให้อันใหม่ หน้าจอจึงอ่านสถานะใหม่จาก
 *    **คำตอบ** ทุกครั้ง ไม่มีเส้นทาง optimistic update ในไฟล์นี้เลย — การติ๊กธงให้
 *    แถวใหม่โดยไม่แตะแถวเดิมจะแสดงสอง primary พร้อมกัน ซึ่งเป็นสถานะที่ฐานข้อมูล
 *    ไม่มีวันมี และจะทำให้ผู้ดูแลเชื่อว่าตัวเองเพิ่งสร้างมันขึ้นมา
 * 3. **สิทธิ์เข้าถึงโรงงานคือ plant scope ของคนคนนั้น (D-004)** — ให้/ถอนแล้วเปลี่ยน
 *    ทันทีว่าเขาเห็นข้อมูลของโรงงานใดได้บ้าง
 *
 * ⚠️ **ข้อจำกัดที่ contract กำหนด ไม่ใช่ทางลัดของหน้าจอ**: `GET /admin/employees`
 * คืนเฉพาะ **แผนกหลัก** (`primary_department_*`) และ **รหัสโรงงานล้วน ๆ**
 * (`plant_codes` ไม่มีธง home) — ไม่มี endpoint ใดที่อ่านสังกัดทั้งชุดหรือธง
 * `is_home_plant` ได้ ชุดเต็มมาจากคำตอบของ `POST` เท่านั้น หน้าจอจึงแสดง "สรุปจาก
 * รายการองค์กร" ก่อน แล้วเปลี่ยนเป็น "ชุดที่เซิร์ฟเวอร์ยืนยันหลังเปลี่ยน" เมื่อมี
 * คำตอบจริง — และ **บอกตรง ๆ ว่าอันไหนเป็นอันไหน** แทนที่จะเดาธงที่ไม่เคยได้รับมา
 *
 * ⚠️ ทั้งหมดนี้เป็น UX layer ไม่ใช่ด่านความปลอดภัย เซิร์ฟเวอร์เป็นผู้ตัดสินทุก
 * request เสมอ — ที่นี่สำคัญเป็นพิเศษ เพราะสิ่งที่หน้าจอนี้แก้คือตัวการ์ดเอง
 */

import { useEffect, useState } from 'react';

import { RbacAdminFailureNotice } from './RbacAdminFailureNotice';
import {
  EMPLOYEE_STATUS_DESCRIPTORS,
  countHomePlants,
  countPrimaryDepartments,
  employeeStatusLabel,
  homePlantOf,
  primaryDepartmentOf,
  toCreateEmployeeBody,
  toEmployeeDepartmentBody,
  toEmployeePlantAccessBody,
} from './departmentsView';
import { StatusBadge } from '../StatusBadge';
import { InfoHint } from '../components/InfoHint';
import { can } from '../../lib/permissions/client';
import type { PermissionsState } from '../../lib/permissions/types';
import {
  createEmployee,
  fetchDepartments,
  fetchEmployees,
  issueEmployeeCredential,
  resetEmployeePassword,
  setEmployeeDepartment,
  setEmployeePlantAccess,
  type RbacAdminFailure,
} from '../../lib/rbacAdmin/client';
import {
  ACTIVE_EMPLOYEE_STATUS,
  RBAC_ADMIN_MODULE,
  type Department,
  type Employee,
  type EmployeeDepartments,
  type EmployeePlantAccess,
  type IssuedCredential,
  type PasswordReset,
} from '../../lib/rbacAdmin/types';

export interface EmployeesScreenProps {
  readonly permissions: PermissionsState;
  readonly apiBaseUrl: string;
  /** inject ได้เพื่อการทดสอบ — production ใช้ `fetch` ของเบราว์เซอร์ */
  readonly fetchImpl?: typeof fetch;
}

type DepartmentsState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly items: readonly Department[] }
  | RbacAdminFailure;

type EmployeesState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly items: readonly Employee[] }
  | RbacAdminFailure;

/** คำขอที่กำลังเดินทางอยู่ — ใช้ปิดปุ่มทั้งหน้าจอระหว่างนั้น */
type PendingKind = 'create-employee' | 'assign-department' | 'assign-plant' | 'reset-password' | 'issue-credential';

export function EmployeesScreen({
  permissions,
  apiBaseUrl,
  fetchImpl,
}: EmployeesScreenProps) {
  const canEdit = can(permissions, RBAC_ADMIN_MODULE, 'EDIT');

  const [departments, setDepartments] = useState<DepartmentsState>({ status: 'loading' });
  const [employees, setEmployees] = useState<EmployeesState>({ status: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);
  const [pending, setPending] = useState<PendingKind | null>(null);

  const [newEmployeeId, setNewEmployeeId] = useState('');
  const [newDisplayName, setNewDisplayName] = useState('');
  const [newStatus, setNewStatus] = useState<string>(ACTIVE_EMPLOYEE_STATUS);
  const [createdEmployee, setCreatedEmployee] = useState<Employee | null>(null);
  const [employeeFailure, setEmployeeFailure] = useState<RbacAdminFailure | null>(null);

  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(null);

  /**
   * รีเซ็ตรหัสผ่านโดยผู้ดูแล (D-048): เลือกคน → ยืนยัน → เซิร์ฟเวอร์ตอบรหัสชั่วคราว **ครั้งเดียว**
   * `resetResult` เก็บใน state นี้เท่านั้น (ไม่ลง storage/URL/log) และถูกล้างเมื่อกดปิด
   */
  const [resetTarget, setResetTarget] = useState<Employee | null>(null);
  const [resetResult, setResetResult] = useState<PasswordReset | null>(null);
  const [resetFailure, setResetFailure] = useState<RbacAdminFailure | null>(null);

  /** ออกบัญชีเข้าสู่ระบบแรก (D-177) — รหัสชั่วคราวแสดงครั้งเดียว เก็บใน state นี้เท่านั้น */
  const [issueTarget, setIssueTarget] = useState<Employee | null>(null);
  const [issueUsername, setIssueUsername] = useState('');
  const [issueResult, setIssueResult] = useState<IssuedCredential | null>(null);
  const [issueFailure, setIssueFailure] = useState<RbacAdminFailure | null>(null);
  const [assignDepartmentId, setAssignDepartmentId] = useState('');
  const [assignPrimary, setAssignPrimary] = useState(false);
  const [assignPlantCode, setAssignPlantCode] = useState('');
  const [assignHomePlant, setAssignHomePlant] = useState(false);

  /**
   * ชุดที่ **เซิร์ฟเวอร์ยืนยันแล้ว** ของพนักงานที่เลือกอยู่ — `null` = ยังไม่มีคำตอบ
   *
   * ถูกล้างทิ้งทุกครั้งที่เปลี่ยนคนที่เลือก เพราะสังกัดของคนก่อนหน้าไม่ใช่คำตอบของ
   * คนใหม่ และการปล่อยค้างไว้จะทำให้ผู้ดูแลอ่านแผนกหลักของคนหนึ่งเป็นของอีกคน
   */
  const [assignedDepartments, setAssignedDepartments] = useState<EmployeeDepartments | null>(
    null,
  );
  const [assignedPlants, setAssignedPlants] = useState<EmployeePlantAccess | null>(null);
  const [assignDepartmentFailure, setAssignDepartmentFailure] =
    useState<RbacAdminFailure | null>(null);
  const [assignPlantFailure, setAssignPlantFailure] = useState<RbacAdminFailure | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    // ระหว่างโหลดซ้ำหลังบันทึกสำเร็จ **ตารางเดิมยังอยู่** ไม่กระพริบเป็นหน้าว่าง:
    // ผู้ดูแลเพิ่งกดปุ่มไป สิ่งที่เขาต้องเห็นคือผลของมัน ไม่ใช่หน้าจอที่หายไปทั้งจอ
    setDepartments((previous) =>
      previous.status === 'ready' ? previous : { status: 'loading' },
    );
    setEmployees((previous) => (previous.status === 'ready' ? previous : { status: 'loading' }));

    const options = { apiBaseUrl, fetchImpl, signal: controller.signal };

    void fetchDepartments(options).then((result) => {
      if (!active || result.status === 'cancelled') {
        return;
      }
      setDepartments(
        result.status === 'ready' ? { status: 'ready', items: result.page.items } : result,
      );
    });

    void fetchEmployees(options).then((result) => {
      if (!active || result.status === 'cancelled') {
        return;
      }
      setEmployees(
        result.status === 'ready' ? { status: 'ready', items: result.page.items } : result,
      );
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [apiBaseUrl, fetchImpl, reloadToken]);

  const openIssue = (employee: Employee) => {
    setResetTarget(null);
    setResetResult(null);
    setResetFailure(null);
    setIssueResult(null);
    setIssueFailure(null);
    setIssueUsername(employee.employee_id);
    setIssueTarget(employee);
  };

  const confirmIssue = async () => {
    if (issueTarget === null || pending !== null || !canEdit) {
      return;
    }
    const target = issueTarget;
    setPending('issue-credential');
    setIssueFailure(null);
    setIssueResult(null);
    const result = await issueEmployeeCredential(
      target.employee_id,
      issueUsername.trim() === target.employee_id ? null : issueUsername.trim(),
      { apiBaseUrl, fetchImpl },
    );
    setPending(null);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      setIssueFailure(result);
      return;
    }
    setIssueTarget(null);
    setIssueResult(result.credential);
  };

  const confirmReset = async () => {
    if (resetTarget === null || pending !== null || !canEdit) {
      return;
    }
    const target = resetTarget;
    setPending('reset-password');
    setResetFailure(null);
    setResetResult(null);
    const result = await resetEmployeePassword(target.employee_id, { apiBaseUrl, fetchImpl });
    setPending(null);
    if (result.status === 'cancelled') {
      return;
    }
    setResetTarget(null);
    if (result.status !== 'ready') {
      setResetFailure(result);
      return;
    }
    setResetResult(result.reset);
  };

  const selectEmployee = (employeeId: string) => {
    setSelectedEmployeeId(employeeId);
    setAssignedDepartments(null);
    setAssignedPlants(null);
    setAssignDepartmentFailure(null);
    setAssignPlantFailure(null);
    setAssignDepartmentId('');
    setAssignPrimary(false);
    setAssignPlantCode('');
    setAssignHomePlant(false);
  };

  const submitCreateEmployee = async () => {
    const body = toCreateEmployeeBody(newEmployeeId, newDisplayName, newStatus);
    if (body === null || pending !== null || !canEdit) {
      return;
    }

    setPending('create-employee');
    setCreatedEmployee(null);
    setEmployeeFailure(null);

    const result = await createEmployee(body, { apiBaseUrl, fetchImpl });
    setPending(null);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      setEmployeeFailure(result);
      return;
    }

    setCreatedEmployee(result.employee);
    setNewEmployeeId('');
    setNewDisplayName('');
    setNewStatus(ACTIVE_EMPLOYEE_STATUS);
    setReloadToken((token) => token + 1);
  };

  const submitDepartmentAssignment = async (granted: boolean) => {
    const body = toEmployeeDepartmentBody(assignDepartmentId, assignPrimary, granted);
    if (body === null || selectedEmployeeId === null || pending !== null || !canEdit) {
      return;
    }

    setPending('assign-department');
    setAssignDepartmentFailure(null);

    const result = await setEmployeeDepartment(selectedEmployeeId, body, {
      apiBaseUrl,
      fetchImpl,
    });
    setPending(null);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      // ชุดที่แสดงอยู่ไม่ถูกแตะ — การปฏิเสธแปลว่าไม่มีอะไรเปลี่ยนจริง ๆ
      setAssignDepartmentFailure(result);
      return;
    }

    // **สถานะใหม่มาจากคำตอบ ไม่ใช่จากปุ่มที่เพิ่งกด** — นี่คือจุดที่การ "ย้าย"
    // ธง `is_primary` ของเซิร์ฟเวอร์ปรากฏบนหน้าจอ และเป็นเหตุผลที่ไม่มี
    // optimistic update ที่ไหนเลยในไฟล์นี้
    setAssignedDepartments(result.departments);
    // รายการองค์กรถือ `primary_department_name` ของคนนี้อยู่ด้วย จึงต้องตามมาให้ตรง
    setReloadToken((token) => token + 1);
  };

  const submitPlantAssignment = async (granted: boolean) => {
    const body = toEmployeePlantAccessBody(assignPlantCode, assignHomePlant, granted);
    if (body === null || selectedEmployeeId === null || pending !== null || !canEdit) {
      return;
    }

    setPending('assign-plant');
    setAssignPlantFailure(null);

    const result = await setEmployeePlantAccess(selectedEmployeeId, body, {
      apiBaseUrl,
      fetchImpl,
    });
    setPending(null);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      setAssignPlantFailure(result);
      return;
    }

    setAssignedPlants(result.access);
    setReloadToken((token) => token + 1);
  };

  const departmentItems = departments.status === 'ready' ? departments.items : [];
  const employeeItems = employees.status === 'ready' ? employees.items : [];
  const selectedEmployee =
    selectedEmployeeId === null
      ? null
      : (employeeItems.find((entry) => entry.employee_id === selectedEmployeeId) ?? null);
  const busy = pending !== null;

  const primaryDepartment =
    assignedDepartments === null ? null : primaryDepartmentOf(assignedDepartments.departments);
  const homePlant = assignedPlants === null ? null : homePlantOf(assignedPlants.plant_access);

  return (
    <div className="cap-page" data-testid="rbac-employees">
      <div className="cap-page__head">
        <div>
          <h1 className="cap-page__title">
            พนักงาน{' '}
            <InfoHint
              label="เกี่ยวกับหน้าพนักงาน"
              text={
                <>
                  แผนกคือสิ่งที่ <strong>ตารางสิทธิ์</strong> ผูกอยู่ และโรงงานคือสิ่งที่{' '}
                  <strong>ขอบเขตการมองเห็นข้อมูล</strong> ผูกอยู่ (D-004) — หน้าจอนี้คือที่ที่ทั้ง
                  สองอย่างถูกต่อเข้ากับพนักงานคนหนึ่ง แผนกหลักได้เพียงแผนกเดียวและโรงงานหลัก
                  ได้เพียงโรงงานเดียว การตั้งอันใหม่จึงเป็นการ <strong>ย้าย</strong> ไม่ใช่การเพิ่ม
                </>
              }
            />
          </h1>
        </div>
      </div>

      {!canEdit ? (
        <div className="cap-notice" data-testid="rbac-org-read-only" role="status">
          <StatusBadge tone="warning">ดูได้อย่างเดียว</StatusBadge>
          <h2 className="cap-notice__title">สิทธิ์ชุดนี้ยังสร้างหรือผูกอะไรไม่ได้</h2>
          <p className="cap-notice__text">
            สิทธิ์ที่เซิร์ฟเวอร์ส่งมาไม่มี flag <code className="cap-table__code">can_edit</code>{' '}
            ของโมดูล <code className="cap-table__code">{RBAC_ADMIN_MODULE}</code>{' '}
            ฟอร์มสร้างพนักงาน และปุ่มผูกแผนก/โรงงาน จึงไม่แสดง
          </p>
          <p className="cap-notice__note">
            การซ่อนปุ่มเป็นเพียง UX เพื่อไม่ให้กดสิ่งที่จะถูกปฏิเสธ — ผู้ตัดสินจริงคือ
            backend ทุกคำขอเสมอ
          </p>
        </div>
      ) : null}

      {/* ------------------------------------------------------- สร้างพนักงาน -- */}

      {canEdit ? (
        <form
          className="cap-filters"
          data-testid="rbac-employee-create-form"
          aria-label="สร้างพนักงานใหม่"
          onSubmit={(event) => {
            event.preventDefault();
            void submitCreateEmployee();
          }}
        >
          <label className="cap-field">
            <span className="cap-field__label">รหัสพนักงาน</span>
            <input
              className="cap-input"
              name="employee_id"
              data-testid="rbac-new-employee-id"
              value={newEmployeeId}
              autoComplete="off"
              onChange={(event) => {
                setNewEmployeeId(event.target.value);
              }}
            />
            <InfoHint
              label="เกี่ยวกับรหัสพนักงาน"
              text="รหัสที่องค์กรกำหนดเอง (D-039) ไม่ใช่ตัวเลขที่ระบบสร้าง — รหัสที่มีเจ้าของ อยู่แล้วจะถูกปฏิเสธด้วย 409"
            />
          </label>

          <label className="cap-field">
            <span className="cap-field__label">ชื่อที่แสดง</span>
            <input
              className="cap-input"
              name="display_name"
              data-testid="rbac-new-display-name"
              value={newDisplayName}
              autoComplete="off"
              onChange={(event) => {
                setNewDisplayName(event.target.value);
              }}
            />
          </label>

          <label className="cap-field">
            <span className="cap-field__label">สถานะ</span>
            <select
              className="cap-input"
              name="status"
              data-testid="rbac-new-employee-status"
              value={newStatus}
              onChange={(event) => {
                setNewStatus(event.target.value);
              }}
            >
              {EMPLOYEE_STATUS_DESCRIPTORS.map((status) => (
                <option key={status.code} value={status.code}>
                  {status.label}
                </option>
              ))}
            </select>
          </label>

          <button
            type="submit"
            className="cap-button cap-button--primary"
            data-testid="rbac-employee-create-submit"
            disabled={busy || newEmployeeId.trim() === '' || newDisplayName.trim() === ''}
          >
            {pending === 'create-employee' ? 'กำลังสร้างพนักงาน…' : 'สร้างพนักงาน'}
          </button>
        </form>
      ) : null}

      {/**
       * "ยังล็อกอินไม่ได้" ต้องอยู่ **ตรงจุดที่ผู้ดูแลเพิ่งกดสร้าง** ไม่ใช่เชิงอรรถ
       *
       * การสร้าง employee master กับการออก username/รหัสผ่านเป็นคนละเรื่องและคนละ
       * ตาราง (`employee_credential`) — ผู้ดูแลที่ไม่รู้ข้อนี้จะบอกพนักงานให้ไป
       * ล็อกอิน แล้วทั้งคู่จะเสียเวลาไปกับรหัสผ่านที่ไม่เคยมีอยู่
       */}
      {createdEmployee === null ? null : (
        <div className="cap-notice" data-testid="rbac-employee-create-result" role="status">
          <StatusBadge tone="success">สร้างแล้ว</StatusBadge>
          <h2 className="cap-notice__title">
            สร้างพนักงาน {createdEmployee.display_name} ({createdEmployee.employee_id}) แล้ว —
            สถานะ {employeeStatusLabel(createdEmployee.status)}
          </h2>
          <p className="cap-notice__text" data-testid="rbac-employee-no-login">
            <strong>พนักงานคนนี้ยังเข้าสู่ระบบไม่ได้</strong> — คำขอนี้สร้างเฉพาะข้อมูล
            พนักงาน ไม่ได้สร้างชื่อผู้ใช้หรือรหัสผ่านให้ ต้องออก credential ให้เขาผ่าน
            เส้นทางของมันเองก่อน เขาจึงจะล็อกอินได้ (แต่ถูกอ้างเป็นผู้กระทำในเอกสาร
            ย้อนหลังได้แล้วตั้งแต่ตอนนี้)
          </p>
          <p className="cap-notice__note">
            และเขายัง <strong>ไม่สังกัดแผนกใด ไม่ถือบทบาทระบบใด และยังไม่เห็นโรงงานไหน</strong>{' '}
            เลย — ทั้งสามอย่างเป็นคำขอของมันเอง เริ่มจากผูกแผนกและโรงงานให้ที่รายการด้านล่าง
          </p>
        </div>
      )}

      {employeeFailure === null ? null : (
        <RbacAdminFailureNotice failure={employeeFailure} context="create-employee" />
      )}

      {/* ------------------------------------------------------ รายการพนักงาน -- */}

      {employees.status === 'loading' ? (
        <div className="cap-notice" data-testid="rbac-employees-loading" role="status">
          <p className="cap-notice__text">กำลังโหลดรายชื่อพนักงาน…</p>
        </div>
      ) : null}

      {employees.status !== 'loading' && employees.status !== 'ready' ? (
        <RbacAdminFailureNotice
          failure={employees}
          context="employees"
          onRetry={() => {
            setReloadToken((token) => token + 1);
          }}
        />
      ) : null}

      {employees.status === 'ready' && employeeItems.length === 0 ? (
        <div className="cap-notice" data-testid="rbac-employees-empty" role="status">
          <StatusBadge tone="neutral">ยังไม่มีพนักงาน</StatusBadge>
          <h2 className="cap-notice__title">ยังไม่มีพนักงานในระบบเลย</h2>
          <p className="cap-notice__text">
            เซิร์ฟเวอร์ตอบสำเร็จแต่รายการว่างเปล่า — ไม่ใช่การโหลดที่ล้มเหลว
          </p>
        </div>
      ) : null}

      {canEdit && resetTarget !== null ? (
        <section className="cap-notice" data-testid="rbac-reset-confirm" role="alertdialog">
          <StatusBadge tone="warning">ยืนยันก่อนรีเซ็ต</StatusBadge>
          <h2 className="cap-notice__title">
            รีเซ็ตรหัสผ่านของ {resetTarget.display_name} (
            <code className="cap-table__code">{resetTarget.employee_id}</code>)?
          </h2>
          <p className="cap-notice__text">
            ระบบจะสร้าง<strong>รหัสผ่านชั่วคราว</strong>ให้และแสดง<strong>ครั้งเดียว</strong>{' '}
            ทุก session ของพนักงานคนนี้ถูกยุติทันที และเขาต้องตั้งรหัสผ่านใหม่เมื่อเข้าสู่ระบบ
            รหัสเดิมใช้ไม่ได้อีก
          </p>
          <div className="cap-notice__actions">
            <button
              type="button"
              className="cap-button cap-button--primary"
              data-testid="rbac-reset-confirm-yes"
              disabled={pending !== null}
              onClick={() => {
                void confirmReset();
              }}
            >
              {pending === 'reset-password' ? 'กำลังรีเซ็ต…' : 'ยืนยัน รีเซ็ตรหัสผ่าน'}
            </button>
            <button
              type="button"
              className="cap-button cap-button--ghost"
              data-testid="rbac-reset-confirm-no"
              disabled={pending !== null}
              onClick={() => {
                setResetTarget(null);
              }}
            >
              ยกเลิก
            </button>
          </div>
        </section>
      ) : null}

      {canEdit && issueTarget !== null ? (
        <section className="cap-notice" data-testid="rbac-issue-confirm" role="alertdialog">
          <StatusBadge tone="info">ออกบัญชีเข้าสู่ระบบ</StatusBadge>
          <h2 className="cap-notice__title">
            ออกบัญชีให้ {issueTarget.display_name} (
            <code className="cap-table__code">{issueTarget.employee_id}</code>)
          </h2>
          <p className="cap-notice__text">
            ใช้กับพนักงานที่<strong>ยังไม่เคยมีบัญชี</strong> ระบบสร้างรหัสผ่านชั่วคราวให้และแสดง
            <strong>ครั้งเดียว</strong> พนักงานต้องตั้งรหัสผ่านใหม่ตอนเข้าสู่ระบบครั้งแรก
            ถ้ามีบัญชีอยู่แล้วให้ใช้ &quot;รีเซ็ตรหัสผ่าน&quot; แทน
          </p>
          <label className="cap-field">
            <span className="cap-field__label">ชื่อผู้ใช้ (username)</span>
            <input
              className="cap-input"
              data-testid="rbac-issue-username"
              autoComplete="off"
              value={issueUsername}
              onChange={(event) => {
                setIssueUsername(event.target.value);
              }}
            />
          </label>
          <div className="cap-notice__actions">
            <button
              type="button"
              className="cap-button cap-button--primary"
              data-testid="rbac-issue-confirm-yes"
              disabled={pending !== null || issueUsername.trim() === ''}
              onClick={() => {
                void confirmIssue();
              }}
            >
              {pending === 'issue-credential' ? 'กำลังออกบัญชี…' : 'ยืนยัน ออกบัญชี'}
            </button>
            <button
              type="button"
              className="cap-button cap-button--ghost"
              data-testid="rbac-issue-confirm-no"
              disabled={pending !== null}
              onClick={() => {
                setIssueTarget(null);
                setIssueFailure(null);
              }}
            >
              ยกเลิก
            </button>
          </div>
        </section>
      ) : null}

      {issueResult === null ? null : (
        <section className="cap-notice" data-testid="rbac-issue-result" role="status">
          <StatusBadge tone="success">ออกบัญชีแล้ว</StatusBadge>
          <h2 className="cap-notice__title">
            บัญชีของ <code className="cap-table__code">{issueResult.employee_id}</code>
          </h2>
          <p className="cap-notice__text">
            ชื่อผู้ใช้:{' '}
            <code className="cap-table__code" data-testid="rbac-issue-result-username">
              {issueResult.username}
            </code>
          </p>
          <p className="cap-notice__text">
            รหัสผ่านชั่วคราว:{' '}
            <code className="cap-table__code" data-testid="rbac-issue-result-password">
              {issueResult.temporary_password}
            </code>
          </p>
          <p className="cap-notice__text">
            <strong>แสดงครั้งเดียว</strong> — ปิดแล้วดูซ้ำไม่ได้ แจ้งพนักงานผ่านช่องทางที่ปลอดภัย
            เขาต้องเปลี่ยนรหัสผ่านทันทีที่เข้าสู่ระบบ ตอนนี้ยังไม่มีสิทธิ์เข้าแอปอื่น (SSO) หรือโรงงานใด
            ต้องตั้งแยกที่หน้าที่เกี่ยวข้อง
          </p>
          <div className="cap-notice__actions">
            <button
              type="button"
              className="cap-button cap-button--primary"
              data-testid="rbac-issue-result-close"
              onClick={() => {
                setIssueResult(null);
              }}
            >
              ปิด (จดรหัสแล้ว)
            </button>
          </div>
        </section>
      )}

      {issueFailure === null ? null : (
        <div
          className="cap-notice"
          data-testid="rbac-issue-failure"
          data-failure={issueFailure.status}
          role="status"
        >
          <StatusBadge tone="danger">ออกบัญชีไม่สำเร็จ</StatusBadge>
          <p className="cap-notice__text">
            {issueFailure.status === 'conflict'
              ? 'ชื่อผู้ใช้นี้มีคนใช้แล้ว หรือพนักงานคนนี้มีบัญชีอยู่แล้ว (ใช้ "รีเซ็ตรหัสผ่าน" แทน) — ไม่มีอะไรเปลี่ยน'
              : issueFailure.status === 'rule-violation'
                ? 'ชื่อผู้ใช้ใช้ได้เฉพาะตัวอักษร ตัวเลข และ . _ @ - หรือพนักงานคนนี้ไม่ได้อยู่ในสถานะใช้งาน — ไม่มีอะไรเปลี่ยน'
                : issueFailure.status === 'forbidden'
                  ? 'บัญชีนี้ไม่มีสิทธิ์ออกบัญชี (ต้องเป็น ADMIN) — ไม่มีอะไรเปลี่ยน'
                  : 'ทำรายการไม่สำเร็จ ไม่มีรหัสผ่านใหม่ถูกแสดง โหลดหน้าใหม่แล้วลองอีกครั้ง'}
          </p>
        </div>
      )}

      {resetResult === null ? null : (
        <section className="cap-notice" data-testid="rbac-reset-result" role="status">
          <StatusBadge tone="success">รีเซ็ตแล้ว</StatusBadge>
          <h2 className="cap-notice__title">
            รหัสผ่านชั่วคราวของ <code className="cap-table__code">{resetResult.employee_id}</code>
          </h2>
          <p className="cap-notice__text">
            <code className="cap-table__code" data-testid="rbac-reset-temporary-password">
              {resetResult.temporary_password}
            </code>
          </p>
          <p className="cap-notice__text">
            <strong>แสดงครั้งเดียว</strong> — ปิดแผงนี้แล้วดูซ้ำไม่ได้ แจ้งพนักงานผ่านช่องทางที่ปลอดภัย
            เขาต้องเปลี่ยนรหัสผ่านทันทีที่เข้าสู่ระบบ ยุติ session ของเขาไปแล้ว{' '}
            {resetResult.revoked_session_count} รายการ
          </p>
          <div className="cap-notice__actions">
            <button
              type="button"
              className="cap-button cap-button--primary"
              data-testid="rbac-reset-result-close"
              onClick={() => {
                setResetResult(null);
              }}
            >
              ปิด (จดรหัสแล้ว)
            </button>
          </div>
        </section>
      )}

      {resetFailure === null ? null : (
        <div
          className="cap-notice"
          data-testid="rbac-reset-failure"
          data-failure={resetFailure.status}
          role="status"
        >
          <StatusBadge tone="danger">รีเซ็ตรหัสผ่านไม่สำเร็จ</StatusBadge>
          <p className="cap-notice__text">
            {resetFailure.status === 'not-found'
              ? 'พนักงานคนนี้ยังไม่มีบัญชีเข้าสู่ระบบ (ยังไม่เคยออกรหัสผ่าน) จึงไม่มีรหัสให้รีเซ็ต — ใช้ปุ่ม "ออกบัญชีเข้าสู่ระบบ" ที่แถวเดียวกันก่อน ไม่มีอะไรเปลี่ยน'
              : resetFailure.status === 'forbidden'
                ? 'บัญชีนี้ไม่มีสิทธิ์รีเซ็ตรหัสผ่าน (ต้องเป็น ADMIN) — ไม่มีอะไรเปลี่ยน'
                : resetFailure.status === 'unauthenticated'
                  ? 'เซสชันหมดอายุ เข้าสู่ระบบใหม่แล้วลองอีกครั้ง — ไม่มีอะไรเปลี่ยน'
                  : 'ทำรายการไม่สำเร็จ ไม่มีรหัสผ่านใหม่ถูกแสดง โหลดหน้าใหม่แล้วลองอีกครั้ง'}
          </p>
        </div>
      )}

      {employees.status === 'ready' && employeeItems.length > 0 ? (
        <section className="cap-card cap-card--table" aria-label="พนักงานทั้งหมด">
          <div className="cap-card__head">
            <h2 className="cap-card__title">
              พนักงานทั้งหมด{' '}
              <InfoHint
                label="เกี่ยวกับตารางพนักงาน"
                text={
                  <>
                    รายการนี้บอก <strong>แผนกหลัก</strong> และรหัสโรงงานที่เข้าถึงได้ — ไม่ได้บอก
                    แผนกทั้งหมดที่สังกัด และไม่ได้บอกว่าโรงงานใดเป็นโรงงานหลัก
                  </>
                }
              />
            </h2>
          </div>
          <div className="cap-table-wrap">
            <table className="cap-table" data-testid="rbac-employees-table">
              <caption className="cap-visually-hidden">
                พนักงานทุกคนในองค์กร พร้อมแผนกหลัก บทบาทระบบ และโรงงานที่เข้าถึงได้
              </caption>
              <thead>
                <tr>
                  <th scope="col">พนักงาน</th>
                  <th scope="col">สถานะ</th>
                  <th scope="col">แผนกหลัก</th>
                  <th scope="col">บทบาทระบบ</th>
                  <th scope="col">โรงงานที่เข้าถึงได้</th>
                  <th scope="col">จัดการสังกัด</th>
                  <th scope="col">รหัสผ่าน</th>
                </tr>
              </thead>
              <tbody>
                {employeeItems.map((employee) => (
                  <tr
                    key={employee.employee_id}
                    data-testid={`rbac-employee-row-${employee.employee_id}`}
                    data-selected={employee.employee_id === selectedEmployeeId}
                  >
                    <th scope="row" className="cap-table__row-head">
                      {employee.display_name}
                      <br />
                      <code className="cap-table__code">{employee.employee_id}</code>
                    </th>
                    <td>
                      <StatusBadge
                        tone={employee.status === ACTIVE_EMPLOYEE_STATUS ? 'success' : 'neutral'}
                      >
                        {employeeStatusLabel(employee.status)}
                      </StatusBadge>
                    </td>
                    <td data-testid={`rbac-employee-primary-${employee.employee_id}`}>
                      {employee.primary_department_name ?? 'ไม่สังกัดแผนกใด'}
                    </td>
                    <td>
                      {employee.system_roles.length === 0
                        ? 'ไม่ถือบทบาทระบบ'
                        : employee.system_roles.join(' และ ')}
                    </td>
                    <td data-testid={`rbac-employee-plants-${employee.employee_id}`}>
                      {employee.plant_codes.length === 0
                        ? 'ยังไม่มีโรงงานใด'
                        : employee.plant_codes.join(', ')}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="cap-button cap-button--ghost"
                        data-testid={`rbac-employee-select-${employee.employee_id}`}
                        onClick={() => {
                          selectEmployee(employee.employee_id);
                        }}
                      >
                        เลือก
                      </button>
                    </td>
                    <td>
                      {canEdit ? (
                        <button
                          type="button"
                          className="cap-button cap-button--ghost"
                          data-testid={`rbac-employee-reset-${employee.employee_id}`}
                          disabled={pending !== null}
                          onClick={() => {
                            setIssueTarget(null);
                            setIssueResult(null);
                            setIssueFailure(null);
                            setResetResult(null);
                            setResetFailure(null);
                            setResetTarget(employee);
                          }}
                        >
                          รีเซ็ตรหัสผ่าน
                        </button>
                      ) : (
                        <span className="cap-field__hint">—</span>
                      )}
                      {canEdit ? (
                        <button
                          type="button"
                          className="cap-button cap-button--ghost"
                          data-testid={`rbac-employee-issue-${employee.employee_id}`}
                          disabled={pending !== null}
                          onClick={() => {
                            openIssue(employee);
                          }}
                        >
                          ออกบัญชีเข้าสู่ระบบ
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {/**
       * รายการแผนกที่โหลดไม่สำเร็จเป็นความล้มเหลว **ของตัวเลือกในฟอร์มผูกแผนก**
       *
       * หน้านี้ไม่ได้แสดงตารางแผนก แต่ช่อง "แผนก" ด้านล่างดึงตัวเลือกจากคำขอเส้นนี้
       * — ถ้าเงียบไว้ ผู้ดูแลจะเห็นแต่ dropdown ที่ว่างเปล่าและสรุปเอาเองว่าองค์กรนี้
       * ยังไม่มีแผนกสักแผนก ซึ่งคนละเรื่องกับ "อ่านรายการแผนกไม่ได้"
       */}
      {departments.status !== 'loading' && departments.status !== 'ready' ? (
        <RbacAdminFailureNotice
          failure={departments}
          context="departments"
          onRetry={() => {
            setReloadToken((token) => token + 1);
          }}
        />
      ) : null}

      {/* ------------------------------------- แผงจัดการสังกัดของคนที่เลือก -- */}

      {selectedEmployee === null ? null : (
        <section
          className="cap-card"
          data-testid="rbac-employee-panel"
          aria-label={`สังกัดและสิทธิ์โรงงานของ ${selectedEmployee.display_name}`}
        >
          <div className="cap-card__head">
            <h2 className="cap-card__title">
              {selectedEmployee.display_name} ({selectedEmployee.employee_id}){' '}
              <InfoHint
                label="เกี่ยวกับแผนกหลักและโรงงานหลัก"
                text="แผนกหลักได้แผนกเดียว และโรงงานหลักได้โรงงานเดียว — ตั้งอันใหม่คือการย้าย"
              />
            </h2>
          </div>

          {/* ----------------------------------------------------- แผนก -- */}

          <h3 className="cap-card__title">แผนกที่สังกัด</h3>

          {assignedDepartments === null ? (
            <div className="cap-notice" data-testid="rbac-department-summary" role="status">
              <p className="cap-notice__text">
                แผนกหลักตามรายการองค์กร:{' '}
                <strong>
                  {selectedEmployee.primary_department_name ?? 'ไม่สังกัดแผนกใด'}
                </strong>
              </p>
              <p className="cap-notice__note">
                รายการองค์กรบอกได้เพียงแผนกหลัก — <strong>ชุดสังกัดทั้งหมด</strong>{' '}
                จะปรากฏที่นี่หลังการผูก/ถอนครั้งถัดไป ซึ่งเป็นคำตอบที่เซิร์ฟเวอร์ยืนยันแล้ว
              </p>
            </div>
          ) : (
            <div data-testid="rbac-employee-department-list">
              {assignedDepartments.departments.length === 0 ? (
                <p className="cap-notice__text" data-testid="rbac-employee-departments-empty">
                  ตอนนี้ <strong>ไม่สังกัดแผนกใดเลย</strong> — เป็นสถานะที่ถูกต้อง ผู้ดูแลระบบ
                  และผู้ตรวจสอบไม่จำเป็นต้องมีแผนก (D-009)
                </p>
              ) : (
                <ul className="cap-meta cap-meta--flush">
                  {assignedDepartments.departments.map((link) => (
                    <li
                      key={link.department_id}
                      className="cap-meta__item"
                      data-testid={`rbac-employee-department-${link.department_id}`}
                      data-primary={link.is_primary}
                    >
                      <div className="cap-meta__label">
                        {link.is_primary ? (
                          <span data-testid={`rbac-department-primary-${link.department_id}`}>
                            <StatusBadge tone="info">แผนกหลัก</StatusBadge>
                          </span>
                        ) : (
                          'แผนกที่สังกัด'
                        )}
                      </div>
                      <div className="cap-meta__value">{link.department_name}</div>
                    </li>
                  ))}
                </ul>
              )}
              <p className="cap-notice__note" data-testid="rbac-primary-department-summary">
                แผนกหลักตอนนี้:{' '}
                <strong>{primaryDepartment?.department_name ?? 'ยังไม่มีแผนกหลัก'}</strong> (มี{' '}
                {countPrimaryDepartments(assignedDepartments.departments)} แผนกที่ถูกทำเครื่องหมาย
                — ชุดนี้มาจากคำตอบของเซิร์ฟเวอร์ ไม่ใช่จากปุ่มที่เพิ่งกด)
              </p>
            </div>
          )}

          {canEdit ? (
            <form
              className="cap-filters"
              data-testid="rbac-department-assign-form"
              aria-label="ผูกหรือถอนแผนกของพนักงานคนนี้"
              onSubmit={(event) => {
                event.preventDefault();
                void submitDepartmentAssignment(true);
              }}
            >
              <label className="cap-field">
                <span className="cap-field__label">แผนก</span>
                <select
                  className="cap-input"
                  name="department_id"
                  data-testid="rbac-assign-department-id"
                  value={assignDepartmentId}
                  onChange={(event) => {
                    setAssignDepartmentId(event.target.value);
                  }}
                >
                  <option value="">— เลือกแผนก —</option>
                  {departmentItems.map((department) => (
                    <option key={department.department_id} value={department.department_id}>
                      {department.department_name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="cap-field">
                <input
                  type="checkbox"
                  name="is_primary"
                  data-testid="rbac-assign-department-primary"
                  checked={assignPrimary}
                  onChange={(event) => {
                    setAssignPrimary(event.target.checked);
                  }}
                />
                <span className="cap-field__label">ตั้งเป็นแผนกหลัก</span>
                <InfoHint
                  label="เกี่ยวกับการตั้งแผนกหลัก"
                  text="แผนกหลักเดิม (ถ้ามี) จะถูกปลดในธุรกรรมเดียวกัน — เป็นการย้าย ไม่ใช่การเพิ่ม"
                />
              </label>

              <button
                type="submit"
                className="cap-button cap-button--primary"
                data-testid="rbac-assign-department-grant"
                disabled={busy || assignDepartmentId === ''}
              >
                {pending === 'assign-department' ? 'กำลังบันทึก…' : 'ผูกแผนก'}
              </button>

              <button
                type="button"
                className="cap-button cap-button--ghost"
                data-testid="rbac-assign-department-revoke"
                disabled={busy || assignDepartmentId === ''}
                onClick={() => {
                  void submitDepartmentAssignment(false);
                }}
              >
                ถอนแผนก
              </button>
            </form>
          ) : null}

          {assignDepartmentFailure === null ? null : (
            <RbacAdminFailureNotice
              failure={assignDepartmentFailure}
              context="assign-department"
              employeeId={selectedEmployee.employee_id}
            />
          )}

          {/* --------------------------------------------------- โรงงาน -- */}

          <h3 className="cap-card__title">สิทธิ์เข้าถึงโรงงาน (plant scope — D-004)</h3>

          {assignedPlants === null ? (
            <div className="cap-notice" data-testid="rbac-plant-summary" role="status">
              <p className="cap-notice__text">
                โรงงานที่เข้าถึงได้ตามรายการองค์กร:{' '}
                <strong>
                  {selectedEmployee.plant_codes.length === 0
                    ? 'ยังไม่มีโรงงานใด'
                    : selectedEmployee.plant_codes.join(', ')}
                </strong>
              </p>
              <p className="cap-notice__note">
                รายการองค์กร <strong>ไม่ได้บอกว่าโรงงานใดเป็นโรงงานหลัก</strong> — หน้าจอนี้จึง
                ไม่เดาให้ ธงนั้นจะปรากฏหลังการให้/ถอนสิทธิ์ครั้งถัดไป ซึ่งเป็นคำตอบที่
                เซิร์ฟเวอร์ยืนยันแล้ว
              </p>
            </div>
          ) : (
            <div data-testid="rbac-employee-plant-list">
              {assignedPlants.plant_access.length === 0 ? (
                <p className="cap-notice__text" data-testid="rbac-employee-plants-none">
                  ตอนนี้ <strong>เข้าถึงโรงงานไหนไม่ได้เลย</strong> — เห็นข้อมูลธุรกิจของโรงงาน
                  ใดไม่ได้จนกว่าจะได้รับสิทธิ์อย่างน้อยหนึ่งโรงงาน
                </p>
              ) : (
                <ul className="cap-meta cap-meta--flush">
                  {assignedPlants.plant_access.map((grant) => (
                    <li
                      key={grant.plant_code}
                      className="cap-meta__item"
                      data-testid={`rbac-employee-plant-${grant.plant_code}`}
                      data-home={grant.is_home_plant}
                    >
                      <div className="cap-meta__label">
                        {grant.is_home_plant ? (
                          <span data-testid={`rbac-plant-home-${grant.plant_code}`}>
                            <StatusBadge tone="info">โรงงานหลัก</StatusBadge>
                          </span>
                        ) : (
                          'เข้าถึงได้'
                        )}
                      </div>
                      <div className="cap-meta__value">
                        <code className="cap-table__code">{grant.plant_code}</code>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              <p className="cap-notice__note" data-testid="rbac-home-plant-summary">
                โรงงานหลักตอนนี้:{' '}
                <strong>{homePlant?.plant_code ?? 'ยังไม่มีโรงงานหลัก'}</strong> (มี{' '}
                {countHomePlants(assignedPlants.plant_access)} โรงงานที่ถูกทำเครื่องหมาย —
                ชุดนี้มาจากคำตอบของเซิร์ฟเวอร์ ไม่ใช่จากปุ่มที่เพิ่งกด)
              </p>
            </div>
          )}

          {canEdit ? (
            <form
              className="cap-filters"
              data-testid="rbac-plant-assign-form"
              aria-label="ให้หรือถอนสิทธิ์เข้าถึงโรงงานของพนักงานคนนี้"
              onSubmit={(event) => {
                event.preventDefault();
                void submitPlantAssignment(true);
              }}
            >
              <label className="cap-field">
                <span className="cap-field__label">รหัสโรงงาน</span>
                <input
                  className="cap-input"
                  name="plant_code"
                  data-testid="rbac-assign-plant-code"
                  value={assignPlantCode}
                  autoComplete="off"
                  onChange={(event) => {
                    setAssignPlantCode(event.target.value);
                  }}
                />
                <InfoHint
                  label="เกี่ยวกับรหัสโรงงาน"
                  text="รหัสทางธุรกิจ เช่น BKK-01 — กลุ่มหลังบ้านนี้ไม่มีรายการโรงงานให้เลือก รหัสที่ไม่มีอยู่จริงจะถูกปฏิเสธด้วย 422"
                />
              </label>

              <label className="cap-field">
                <input
                  type="checkbox"
                  name="is_home_plant"
                  data-testid="rbac-assign-plant-home"
                  checked={assignHomePlant}
                  onChange={(event) => {
                    setAssignHomePlant(event.target.checked);
                  }}
                />
                <span className="cap-field__label">ตั้งเป็นโรงงานหลัก</span>
                <InfoHint
                  label="เกี่ยวกับการตั้งโรงงานหลัก"
                  text="โรงงานหลักเดิม (ถ้ามี) จะถูกปลดในธุรกรรมเดียวกัน"
                />
              </label>

              <button
                type="submit"
                className="cap-button cap-button--primary"
                data-testid="rbac-assign-plant-grant"
                disabled={busy || assignPlantCode.trim() === ''}
              >
                {pending === 'assign-plant' ? 'กำลังบันทึก…' : 'ให้สิทธิ์โรงงาน'}
              </button>

              <button
                type="button"
                className="cap-button cap-button--ghost"
                data-testid="rbac-assign-plant-revoke"
                disabled={busy || assignPlantCode.trim() === ''}
                onClick={() => {
                  void submitPlantAssignment(false);
                }}
              >
                ถอนสิทธิ์โรงงาน
              </button>
            </form>
          ) : null}

          {assignPlantFailure === null ? null : (
            <RbacAdminFailureNotice
              failure={assignPlantFailure}
              context="assign-plant"
              employeeId={selectedEmployee.employee_id}
            />
          )}
        </section>
      )}
    </div>
  );
}
